import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowLeft, Check, ArrowRight } from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { reload } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) return setErr("Password must be at least 8 characters.");
    if (password !== confirm) return setErr("Passwords don't match.");
    if (!token) return setErr("Missing reset token. Request a new reset link.");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password }, { withCredentials: true });
      setDone(true);
      // Backend already set the auth cookie; refresh in-app state and route.
      try { await reload?.(); } catch {}
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not reset password. The link may have expired.");
    } finally { setBusy(false); }
  };

  return (
    <section className="min-h-[80vh] bg-cream-100 py-16 px-5">
      <div className="max-w-md mx-auto">
        <Link to="/login" className="text-sm text-navy-700/60 hover:text-coral-300 inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="bg-white rounded-2xl soft-shadow p-8" data-testid="reset-password-card">
          <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Choose a new password</p>
          <h1 className="font-serif text-navy-700 text-3xl mt-2">Set your <span className="italic">new password</span></h1>

          {done ? (
            <div data-testid="reset-password-done" className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
              <Check className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-emerald-800">Password updated</div>
                <p className="text-sm text-emerald-800/80 mt-1">Redirecting you to your dashboard…</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">New password</label>
                <div className="relative mt-2">
                  <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-700/40" />
                  <input
                    required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-new-input"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300"
                    placeholder="At least 8 characters"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-navy-700">Confirm password</label>
                <div className="relative mt-2">
                  <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-700/40" />
                  <input
                    required type="password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm-input"
                    className="w-full pl-10 pr-4 py-3 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300"
                    placeholder="Type it again"
                  />
                </div>
              </div>
              {err && <div data-testid="reset-password-error" className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
              <button disabled={busy} type="submit" data-testid="reset-password-submit-btn"
                className="w-full bg-coral-300 text-white py-3 rounded-full font-semibold hover:bg-coral-400 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {busy ? "Updating..." : <>Update password <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ResetPasswordPage;
